<template>
  <v-card class="CardCircularGraphics mt-4" text elevation="0">
    <v-card-title class="pa-0 pb-4">{{$t("CardCircularGraphics.geolocatedView")}}</v-card-title>
    <v-container fluid class="Content d-flex pa-0">
      <div class="Options overflow-hidden pr-1 mr-3">
        <h3 class="headline mt-2 mb-3">{{$t("CardCircularGraphics.variables")}}</h3>
        <div
          v-for="(key) in getOptionsKeys"
          :key="key"
          :class="{IonOption: key === 'io_report'}"
        >
          <h3 v-if="key === 'io_report'" class="headline mt-4 mb-3">{{$t("CardCircularGraphics.irrigationOrders")}}</h3>
          <div
            :class="{Opt: true, Active: options[key].checked}"
            @click="toggleOpt(key)"
          >
            <v-icon v-if="options[key].checked" small color="#0098E1" class="mt-1 fill-height">radio_button_checked</v-icon>
            <v-icon v-else small class="mt-1" >radio_button_unchecked</v-icon>
            <p class="subheading d-inline-block ml-1 mb-2">{{options[key].label}}</p>
          </div>
          <!-- SubOptions -->
          <div
            v-for="(subOpt, i) in options[key].subOptions"
            :key="subOpt.label"
            :class="{Opt: true, Active: (options[key].checked && subOpt.checked), 'ml-4': true}"
            @click="toggleSubOpt(key, i)"
          >
            <v-icon v-if="(options[key].checked && subOpt.checked)" small :color="subOpt.color ? subOpt.color : '#0098E1'" class="mt-1">radio_button_checked</v-icon>
            <v-icon v-else small class="mt-1">radio_button_unchecked</v-icon>
            <p
              class="subheading d-inline-block ml-1 mb-2"
              :style="{color: (options[key].checked && subOpt.checked) ? subOpt.color : ''}"
            >{{subOpt.label}}</p>
          </div>
        </div>
      </div>
      <div class="Graphic fill-height d-flex align-center justify-center">
        <template v-if="checkedOpt.img">
          <div
            v-if="circGraphsData.position"
            class="GraphicImage"
          >
            <div>
              <CardSatelliteImage
                :hasHeatmap="checkedKey === 'altimetry'"
                :position="getCoordinate(circGraphsData.position)"
                :zoom="circGraphsData.zoom"
                :equipLength="circGraphsData.equipLength"
                :heatmapData.sync="heatmapData"
                :elevationReference.sync="elevationReference"
                :addTransparentLayer="checkedKey === 'altimetry' && !!checkedOpt.subOptions.filter(opt => opt.checked).length"
                :mapInstanceId="locationName"
              />
              <p class="AngleLabel Top">
                <span>{{$t("CardCircularGraphics.north")}}</span><br/><span>0°</span>
              </p>
              <p class="AngleLabel Right">90°</p>
              <p class="AngleLabel Bottom">180°</p>
              <p class="AngleLabel Left">270°</p>
            </div>
            <CardPressureChart
                v-if="checkedKey === 'altimetry'"
                :subOptions="checkedOpt.subOptions"
                :circularData="circGraphsData"
                v-on:onClickMarker="setExtraInfoData"
              />
            <template
              v-else
            >
              <CardPieChart
                v-for="subOption in checkedOpt.subOptionsLayers"
                v-if="subOption.checked && subOption.chart === 'pie'"
                :key="subOption.key"
                :optKey="subOption.key"
                :circularData="circGraphsData"
                v-on:onClickSection="setExtraInfoData"
                :selectedFault="selectedFault"
                :selectedIOReport="selectedIOReport"
              />
            </template>
          </div>
          <span v-else class="display-1 text-center grey--text">{{$t("CardCircularGraphics.imageNotAvailable")}}</span>
        </template>
      </div>
      <div class="ExtraInfo d-flex pl-3">
        <div v-if="extraInfoData" class="d-flex justify-space-between">
          <div>
            <p class="body-2" v-for="data in extraInfoData" :key="data[0]" v-html="data[0]"></p>
          </div>
          <div>
            <p class="body-2" v-for="data in extraInfoData" :key="data[1]" v-html="data[1]"></p>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div
          v-if="checkedKey === 'real_image' && options[checkedKey].subOptionsLayers.filter(layer => layer.key === 'faults').length && parsedSelectedFault"
          class="FaultsDescription d-flex mb-5 justify-space-between"
        >
          <div>
            <p class="body-2" v-for="data in parsedSelectedFault" :key="data[0]" v-html="data[0]"></p>
          </div>
          <div>
            <p class="body-2" v-for="data in parsedSelectedFault" :key="data[1]" v-html="data[1]"></p>
          </div>
        </div>

        <div
          v-else-if="checkedKey === 'io_report' && ioReportExtraData"
          class="DegreeCompliance text-center mb-5"
        >
          <p class="subheading">{{$t("CardCircularGraphics.degreeOfCompliance")}}</p>
          <v-badge color="transparent">
            <template v-slot:badge>
              <!-- <v-icon
                small
                color="#F93A2F">report</v-icon> -->
            </template>
            <v-progress-circular
              :rotate="-90"
              :size="80"
              :width="6"
              :value="ioReportExtraData.doc"
              color="#0098E1"
              class="ProgressCircular"
            >
              <span class="headline black--text">{{ioReportExtraData.doc}}%</span>
            </v-progress-circular>
          </v-badge>
          <div class="d-flex text-xs-left mt-3">
            <div class="subheading pr-2">
              <p v-for="data in ioReportExtraData.itemData" :key="data[0]" v-html="data[0]"></p>
            </div>
            <div class="subheading">
              <p v-for="data in ioReportExtraData.itemData" :key="data[0]+data[1]" v-html="data[1]"></p>
            </div>
          </div>
        </div>

        <div
          v-else-if="checkedKey === 'altimetry' && elevationReference"
          class="AltimetryReference d-flex mb-3"
        >
          <div class="ColorReference ml-3"></div>
          <v-layout column class="ElevationValues">
            <p class="ma-1 ml-3 subheading">{{elevationReference.max.toFixed(1)}} m</p>
            <v-spacer></v-spacer>
            <p class="ma-1 ml-3 subheading">{{elevationReference.min.toFixed(1)}} m</p>
          </v-layout>
        </div>
      </div>
    </v-container>

    <!-- Faults Details List -->
    <template v-if="checkedKey === 'real_image' && options[checkedKey].subOptionsLayers.filter(layer => layer.key === 'faults').length">
      <FaultsDetailsList
        :faults="circGraphsData.faults"
        :title="title"
        :locationName="locationName"
        :selectedFault.sync="selectedFault"
      />
    </template>
    <!-- IO Report Details List -->
    <template v-if="checkedKey === 'io_report'" fluid class="IoReport pa-0">
      <IOReportDetailsList
        :ioReportItems="circGraphsData.io_report_items"
        :title="title"
        :locationName="locationName"
        :selectedIOReport.sync="selectedIOReport"
      />
    </template>
  </v-card>
</template>

<script>
import CardPieChart from './CircularGraphicsComponents/PieChart'
import CardPressureChart from './CircularGraphicsComponents/PressureChart'
import CardSatelliteImage from './CircularGraphicsComponents/SatelliteImage'
import FaultsDetailsList from './CircularGraphicsComponents/FaultsDetailsList'
import IOReportDetailsList from './CircularGraphicsComponents/IOReportDetailsList'
import {getCoordinate} from '../../lib/helpers'
import { trackEvent } from '../../lib/track-events'
import { EVENTS_TO_TRACK } from '../../constants/const'

const valOrDash = (val, toFix = false) => {
  let result = val || val === 0 ? val : '-'
  if (result !== '-' && toFix) {
    result = val.toFixed(toFix)
  }
  return result
}

export default {
  data () {
    const getOptionsInitialData = () => ({
      real_image: {
        label: this.$t('CardCircularGraphics.realImage'),
        key: 'real_image',
        checked: true,
        img: true,
        subOptionsLayers: [],
        subOptions: [{
          key: 'homogeneity',
          label: this.$t('CardCircularGraphics.applicationRateHomogeneity'),
          checked: false,
          chart: 'pie'
        }, {
          key: 'faults',
          label: this.$t('CardCircularGraphics.faults'),
          checked: false,
          chart: 'pie',
          details: [{
            code: 'DFX12',
            angle: '210°',
            duration: '1h 12m',
            start_end: '3/9 11:32 am\n3/9 00:44 pm',
            type: `${this.$t('CardCircularGraphics.pump')} ON\n${this.$t('CardCircularGraphics.equipment')} OFF`,
            recover_time: '13h 10m',
            details: this.$t('CardCircularGraphics.fourthTowerFail')
          }, {
            code: 'DFX13',
            angle: '297° a 355°',
            duration: '8h 12m',
            start_end: '11/9 10:32 am\n11/9 6:44 pm',
            type: `${this.$t('CardCircularGraphics.pump')} ON\n${this.$t('CardCircularGraphics.equipment')} OFF`,
            recover_time: '1h 10m',
            details: this.$t('CardCircularGraphics.lowTension')
          }]
        }]
      },
      altimetry: {
        label: this.$t('CardCircularGraphics.altimetry'),
        key: 'altimetry',
        checked: false,
        img: true,
        subOptions: [{
          key: 'chart-pressure',
          label: this.$t('CardCircularGraphics.chartPressure'),
          checked: true,
          color: '#000000'
        }, {
          key: 'min-pressure',
          label: this.$t('CardCircularGraphics.minimumPressure'),
          checked: true,
          color: '#F93A2F'
        }, {
          key: 'avg-pressure',
          label: this.$t('CardCircularGraphics.averagePressure'),
          checked: true,
          color: '#0098E1'
        }, {
          key: 'max-pressure',
          label: this.$t('CardCircularGraphics.maximumPressure'),
          checked: true,
          color: '#52B847'
        }]
      },
      io_report: {
        label: this.$t('CardCircularGraphics.irrigationOrderReport'),
        key: 'io_report',
        checked: false,
        chart: 'pie',
        degree_compliance: true,
        details: [{
          doc: 'check',
          emission: '3/9',
          code: 'LGP2001',
          transmitter: 'Francisco Lodos',
          millimeters: 10,
          start_end: '4/9 - 5/9',
          start: '4/9 8:35 am',
          application_rate: 12,
          observations: ''
        }, {
          doc: 80,
          emission: '7/9',
          code: 'LGP2002',
          transmitter: 'Francisco Lodos',
          millimeters: 20,
          start_end: '7/9 - 10/9',
          start: '7/9 5:35 pm',
          application_rate: 21,
          observations: this.$t('CardCircularGraphics.lotNotComplete')
        }, {
          doc: 55,
          emission: '10/9',
          code: 'LGP2003',
          transmitter: 'Francisco Lodos',
          millimeters: 15,
          start_end: '10/9 - 12/9',
          start: '10/9 8:35 am',
          application_rate: 17,
          observations: this.$t('CardCircularGraphics.DFX12Failure')
        }, {
          doc: 'check',
          emission: '16/9',
          code: 'LGP2004',
          transmitter: 'Francisco Lodos',
          millimeters: 20,
          start_end: '16/9 - 18/9',
          start: '16/9 8:35 am',
          application_rate: 21,
          observations: ''
        }, {
          doc: 'check',
          emission: '18/9',
          code: 'LGP2005',
          transmitter: 'Francisco Lodos',
          millimeters: 10,
          start_end: '18/9 - 19/9',
          start: '18/9 8:35 am',
          application_rate: 12,
          observations: ''
        }]
      }
    })
    return {
      options: getOptionsInitialData(),
      extraInfoData: null,
      keystoneUrl: process.env.REPORTS_KEYSTONE_URL,
      heatmapData: null,
      selectedFault: null,
      selectedIOReport: null,
      elevationReference: null,
      getCoordinate
    }
  },
  props: ['locationName', 'circGraphsData', 'title'],
  components: {
    CardPieChart,
    CardPressureChart,
    CardSatelliteImage,
    FaultsDetailsList,
    IOReportDetailsList
  },
  methods: {
    toggleOpt (key) {
      if (!this.options[key].checked) {
        for (let opt in this.options) {
          this.options[opt].checked = false
        }

        this.options[key].checked = true

        this.extraInfoData = null
        this.selectedFault = null
        trackEvent(EVENTS_TO_TRACK.CLICK_SELECT_CIRCULAR_MAP_LAYER_OPTION, {option: this.options[key].key})
      }
    },
    toggleSubOpt (key, index) {
      if (this.options[key].checked) {
        this.options[key].subOptions[index].checked = !this.options[key].subOptions[index].checked
        if (key === 'real_image') this.orderPieLayers(key, index)
      } else {
        this.options[key].subOptions = this.options[key].subOptions.map((subOpt, i) => ({...subOpt, checked: i === index}))
        this.toggleOpt(key)
      }
      if (this.options[key].subOptions[index].checked) {
        trackEvent(EVENTS_TO_TRACK.CLICK_SELECT_CIRCULAR_MAP_LAYER_SUBOPTION, {subOption: this.options[key].subOptions[index].key})
      } else {
        trackEvent(EVENTS_TO_TRACK.CLICK_UNSELECT_CIRCULAR_MAP_LAYER_SUBOPTION, {subOption: this.options[key].subOptions[index].key})
      }
    },
    setExtraInfoData (data) {
      this.extraInfoData = data
    },
    orderPieLayers (key, index) {
      if (this.options[key].subOptions[index].checked) {
        this.options[key].subOptionsLayers.push(this.options[key].subOptions[index])
      } else {
        this.options[key].subOptionsLayers = this.options[key].subOptionsLayers.filter(subOption => subOption.key !== this.options[key].subOptions[index].key)
      }
    }
  },
  computed: {
    getOptionsKeys () {
      const keys = []
      for (let key in this.options) {
        keys.push(key)
      }
      return keys
    },
    checkedKey () {
      for (let key in this.options) {
        if (this.options[key].checked) {
          return key
        }
      }
    },
    checkedOpt () {
      for (let key in this.options) {
        if (this.options[key].checked) {
          return this.options[key]
        }
      }
    },
    ioReportExtraData () {
      const ioReportItems = this.circGraphsData.io_report_items
      if (this.selectedIOReport) {
        return {
          doc: this.selectedIOReport.doc,
          itemData: [
            [this.$t('CardCircularGraphics.passes'), valOrDash(this.selectedIOReport.passes, 1)],
            [this.$t('CardCircularGraphics.averageApplicationRate'), valOrDash(this.selectedIOReport.application_rate, 1) + ` ${this.$t('CardCircularGraphics.mmUnit')}<br/>&nbsp;`],
            [this.$t('CardCircularGraphics.emitter'), this.selectedIOReport.transmitter]
          ]
        }
      } else if (ioReportItems && ioReportItems.length > 0) {
        let averageDoc = 0
        let iDoc = 0
        let sumIrrigation = 0
        ioReportItems.forEach(item => {
          if (item.doc) {
            averageDoc += parseFloat(item.doc)
            iDoc++
          }
          if (item.unordered_irrigation) {
            sumIrrigation += parseFloat(item.unordered_irrigation)
          }
        })
        averageDoc = iDoc ? averageDoc / iDoc : 0
        averageDoc = Math.floor(averageDoc)

        return {
          doc: averageDoc,
          itemData: [
            [this.$t('CardCircularGraphics.quantity'), ioReportItems.length],
            [this.$t('CardCircularGraphics.notOrderedIrrigation'), sumIrrigation.toFixed(1) + ` ${this.$t('CardCircularGraphics.mmUnit')}`]
          ]
        }
      }
      return null
    },
    parsedSelectedFault () {
      if (!this.selectedFault) {
        return null
      }
      const parsedData = [
        [`${this.$t('CardCircularGraphics.angle')}:`, `${this.selectedFault.from_angle}°-${this.selectedFault.to_angle}°`],
        [`${this.$t('CardCircularGraphics.failure')}:`, this.selectedFault.type],
        [`${this.$t('CardCircularGraphics.start')}:`, this.selectedFault.start],
        [`${this.$t('CardCircularGraphics.end')}:`, this.selectedFault.end]
      ]
      return parsedData
    }
  }
}
</script>


// WEBPACK FOOTER //
// CardCircularGraphics.vue?1a5aec36